import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Checkbox, Dropdown } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import ProductCard from "./ProductListCards";
import { StatisticsDashboard } from "./StatisticsDashboard";
import useColumnVisibility from "../../../hooks/useColumnVisibility";

const PRODUCT_COLUMNS_DEFAULT = {
  graph: true,
  brand: true,
  photo: true,
  product_name: true,
  root_category: true,
  category_2: true,
  category_3: true,
  category_4: true,
  sellers_count: true,
  price: true,
  sells: true,
  gmv: true,
};

const PRODUCT_COLUMN_LABELS = [
  { key: "graph", label: "График" },
  { key: "brand", label: "Бренд" },
  { key: "photo", label: "Фото" },
  { key: "product_name", label: "Наименование" },
  { key: "root_category", label: "Категория 1" },
  { key: "category_2", label: "Категория 2" },
  { key: "category_3", label: "Категория 3" },
  { key: "category_4", label: "Категория 4" },
  { key: "sellers_count", label: "Кол-во продавцов" },
  { key: "price", label: "Цена" },
  { key: "sells", label: "Заказы" },
  { key: "gmv", label: "Выручка" },
];

const ProductList = ({
  filters,
  sorting,
  shouldSearch,
  onSearchComplete,
  isSearching,
  onBrandFilter,
}) => {
  const { visibleColumns, toggleColumn } = useColumnVisibility(
    "product_analytics",
    PRODUCT_COLUMNS_DEFAULT
  );
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    page_size: 24,
    has_next: false,
    has_previous: false,
  });
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [aggregates, setAggregates] = useState(null);

  const itemsPerPage = 24;

  // Handle column sorting
  const handleSort = (field) => {
    let newDirection = "asc";

    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    }

    setSortField(field);
    setSortDirection(newDirection);

    setCurrentPage(1);
    setProducts([]);

    fetchProducts(1, field, newDirection);
  };

  // Function to fetch monthly analytics data
  const fetchMonthlyData = async (filters) => {
    try {
      const token = localStorage.getItem("token");

      const params = {};

      if (filters.searchValue) params.sku = filters.searchValue;
      if (filters.brand) params.brand = filters.brand;
      if (filters.name) params.product_name = filters.name;
      if (filters.category) params.root_category = filters.category;
      if (filters.subcategory) params.category_2 = filters.subcategory;
      if (filters.subgroup) params.category_3 = filters.subgroup;
      if (filters.type) params.category_4 = filters.type;
      if (filters.excludeBrand) params.exclude_brand = filters.excludeBrand;
      if (filters.excludeCategory)
        params.exclude_root_category = filters.excludeCategory;
      if (filters.excludeSubcategory)
        params.exclude_category_2 = filters.excludeSubcategory;
      if (filters.excludeSubgroup)
        params.exclude_category_3 = filters.excludeSubgroup;
      if (filters.excludeType) params.exclude_category_4 = filters.excludeType;
      if (filters.dateRange?.from) params.from_date = filters.dateRange.from;
      if (filters.dateRange?.to) params.to_date = filters.dateRange.to;
      if (filters.price?.min) params.min_price = filters.price.min;
      if (filters.price?.max) params.max_price = filters.price.max;
      if (filters.sellers?.min) params.min_sellers_count = filters.sellers.min;
      if (filters.sellers?.max) params.max_sellers_count = filters.sellers.max;
      if (filters.orders?.min) params.min_sells_count = filters.orders.min;
      if (filters.orders?.max) params.max_sells_count = filters.orders.max;
      if (filters.revenue?.min) params.min_gmv = filters.revenue.min;
      if (filters.revenue?.max) params.max_gmv = filters.revenue.max;

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_API}/products-analytics/filter-products-monthly/`,
        {
          params,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, item));
              } else if (value !== null && value !== undefined) {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.monthly_analytics || [];
    } catch (error) {
      console.error("Error fetching monthly data:", error);
      throw error;
    }
  };

  const fetchProducts = async (
    page = 1,
    sortFieldParam = null,
    sortDirectionParam = null
  ) => {
    const token = localStorage?.getItem("token");
    if (!token) {
      setError("Токен аутентификации не найден");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        page_size: itemsPerPage,
      };

      // Add filters to params if they exist
      if (filters.searchValue) params.sku = filters.searchValue; // SKU search parameter
      if (filters.brand) params.brand = filters.brand;
      if (filters.name) params.product_name = filters.name;
      if (filters.category) params.root_category = filters.category;
      if (filters.subcategory) params.category_2 = filters.subcategory;
      if (filters.subgroup) params.category_3 = filters.subgroup;
      if (filters.type) params.category_4 = filters.type;
      if (filters.excludeBrand) params.exclude_brand = filters.excludeBrand;
      if (filters.excludeCategory)
        params.exclude_root_category = filters.excludeCategory;
      if (filters.excludeSubcategory)
        params.exclude_category_2 = filters.excludeSubcategory;
      if (filters.excludeSubgroup)
        params.exclude_category_3 = filters.excludeSubgroup;
      if (filters.excludeType) params.exclude_category_4 = filters.excludeType;
      if (filters.dateRange?.from) params.from_date = filters.dateRange.from;
      if (filters.dateRange?.to) params.to_date = filters.dateRange.to;
      if (filters.price?.min) params.min_price = filters.price.min;
      if (filters.price?.max) params.max_price = filters.price.max;
      if (filters.sellers?.min) params.min_sellers_count = filters.sellers.min;
      if (filters.sellers?.max) params.max_sellers_count = filters.sellers.max;
      if (filters.orders?.min) params.min_sells_count = filters.orders.min;
      if (filters.orders?.max) params.max_sells_count = filters.orders.max;
      if (filters.revenue?.min) params.min_gmv = filters.revenue.min;
      if (filters.revenue?.max) params.max_gmv = filters.revenue.max;

      const currentSortField =
        sortFieldParam !== null ? sortFieldParam : sortField;
      const currentSortDirection =
        sortDirectionParam !== null ? sortDirectionParam : sortDirection;

      if (currentSortField) {
        params.sorting = `${currentSortField}_${currentSortDirection}`;
      } else if (sorting) {
        params.sorting = sorting;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_API}/products-analytics/filter-products/`,
        {
          params,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, item));
              } else if (value !== null && value !== undefined) {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const {
        products: fetchedProducts,
        page: currentPageNum,
        page_size,
        total_pages,
        total_count,
        has_next,
        has_previous,
        aggregates,
      } = response.data;

      // Store aggregates for display
      setAggregates(aggregates);

      // Update products - always replace with current page products
      // Products now include image_url from backend
      setProducts(fetchedProducts);

      // Update total count
      setTotalCount(total_count);

      // Update pagination state
      setPagination({
        current_page: currentPageNum,
        total_pages: total_pages,
        page_size: page_size,
        has_next: has_next,
        has_previous: has_previous,
      });

      setCurrentPage(currentPageNum);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching products:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 401) {
        setError(
          detail ||
            "Сессия истекла. Пожалуйста, войдите заново."
        );
      } else if (status === 403) {
        setError(
          detail ||
            "У вас нет доступа к этой функции. Обратитесь в техподдержку."
        );
      } else {
        setError("Ошибка при загрузке товаров");
      }
    } finally {
      setLoading(false);
      if (onSearchComplete) {
        onSearchComplete();
      }
    }
  };

  // Helper function to render sortable column header
  const renderSortableHeader = (field, title, className = "") => {
    const isActive = sortField === field;
    const sortIcon = isActive ? (sortDirection === "asc" ? "↑" : "↓") : "↕";

    return (
      <div
        className={`text-center ${className}`}
        style={{
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color 0.2s ease",
          borderRadius: "4px",
          padding: "4px",
        }}
        onClick={() => handleSort(field)}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = isActive ? "#e3f2fd" : "transparent";
        }}
      >
        <span
          className={`tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-[8px] md:tw-text[10px] lg:tw-text-[11px]
    ${isActive ? "tw-text-[#1976d2]" : "tw-text-[#212529]"}
  `}
        >
          {title}
          <span
            className={`
      tw-font-bold tw-text-[8px] md:tw-text[10px] lg:tw-text-[11px]
      ${
        isActive
          ? "tw-opacity-100 tw-text-[#1976d2]"
          : "tw-opacity-60 tw-text-[#00072D]"
      }
    `}
          >
            {sortIcon}
          </span>
        </span>
      </div>
    );
  };

  useEffect(() => {
    if (shouldSearch) {
      fetchProducts(1);
    }
  }, [shouldSearch]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchProducts(newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBrandClick = (brandName) => {
    if (onBrandFilter) {
      onBrandFilter(brandName);
    }
  };

  // Pagination Controls Component
  const PaginationControls = () => {
    if (!pagination || pagination.total_pages <= 1) return null;

    return (
      <div className="d-flex justify-content-start align-items-center mt-4 mb-3">
        <div className="d-flex align-items-center gap-2">
          {pagination.has_previous && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={loading}
              title="Предыдущая страница"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}

          <span className="mx-3">
            Страница {pagination.current_page} из {pagination.total_pages}
          </span>

          {pagination.has_next && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={loading}
              title="Следующая страница"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
        <div className="text-center">
          <h5 className="text-danger">Ошибка</h5>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
      {isSearching ? (
        // Loading state when search is triggered
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p className="mt-2">Загрузка товаров...</p>
        </div>
      ) : !hasSearched ? (
        // Initial state - no search performed yet
        <div className="text-center py-5">
          <h5>Аналитика товаров</h5>
          <p className="text-muted">
            Настройте фильтры и нажмите кнопку "Поиск" для получения результатов
          </p>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5>Результаты поиска товаров</h5>
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted">Найдено: {totalCount} товаров</span>
              <Dropdown
                trigger={["click"]}
                dropdownRender={() => (
                  <div
                    style={{
                      background: "#fff",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                      minWidth: "180px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: "8px",
                        fontSize: "13px",
                        color: "#333",
                      }}
                    >
                      Показать столбцы
                    </div>
                    {PRODUCT_COLUMN_LABELS.map(({ key, label }) => (
                      <div key={key} style={{ marginBottom: "6px" }}>
                        <Checkbox
                          checked={visibleColumns[key]}
                          onChange={() => toggleColumn(key)}
                        >
                          {label}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
              >
                <Button size="small" icon={<SettingOutlined />}>
                  Столбцы
                </Button>
              </Dropdown>
            </div>
          </div>

          {/* Statistics Dashboard */}
          {/* <StatisticsDashboard
            aggregates={aggregates}
            hasSearched={hasSearched}
          /> */}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </div>
              <p className="mt-2">Загрузка товаров...</p>
            </div>
          ) : products.length === 0 && hasSearched ? (
            <div className="text-center py-5">
              <h6>Товары не найдены</h6>
              <p className="text-muted">
                Попробуйте изменить параметры фильтрации
              </p>
            </div>
          ) : (
            <>
              {/* Column Headers */}
              <div className="tw-sticky tw-top-[56px] md:tw-top-[64px] tw-z-[1000] tw-bg-[#f8f9fa] tw-shadow-sm">
                <div className="row mb-3">
                  <div className="col-12">
                    <div
                      className="card border"
                      style={{
                        backgroundColor: "#f8f9fa",
                        position: "sticky",
                        top: 0, // distance from top when sticking
                        zIndex: 1000, // ensures it stays above other elements
                      }}
                    >
                      <div className="card-body py-3">
                        <div
                          className="tw-flex tw-items-center"
                          style={{ minHeight: "45px" }}
                        >
                          {visibleColumns.graph && (
                            <div className="tw-flex-1 tw-text-center">
                              <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                                График
                              </span>
                            </div>
                          )}
                          {visibleColumns.brand &&
                            renderSortableHeader("brand", "Бренд", "tw-flex-1")}
                          {visibleColumns.photo && (
                            <div className="tw-flex-1 tw-text-center">
                              <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                                Фото
                              </span>
                            </div>
                          )}
                          {visibleColumns.product_name &&
                            renderSortableHeader(
                              "product_name",
                              "Наименование товара",
                              "tw-flex-1"
                            )}
                          {visibleColumns.root_category &&
                            renderSortableHeader(
                              "root_category",
                              "Категория 1",
                              "tw-flex-1"
                            )}
                          {visibleColumns.category_2 &&
                            renderSortableHeader(
                              "category_2",
                              "Категория 2",
                              "tw-flex-1"
                            )}
                          {visibleColumns.category_3 &&
                            renderSortableHeader(
                              "category_3",
                              "Категория 3",
                              "tw-flex-1"
                            )}
                          {visibleColumns.category_4 &&
                            renderSortableHeader(
                              "category_4",
                              "Категория 4",
                              "tw-flex-1"
                            )}
                          {visibleColumns.sellers_count &&
                            renderSortableHeader(
                              "sellers_count",
                              "Кол-во продавцов",
                              "tw-flex-1"
                            )}
                          {visibleColumns.price &&
                            renderSortableHeader("price", "Цена", "tw-flex-1")}
                          {visibleColumns.sells &&
                            renderSortableHeader("sells", "Заказы", "tw-flex-1")}
                          {visibleColumns.gmv &&
                            renderSortableHeader("gmv", "Выручка", "tw-flex-1")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Cards */}
              <div className="row">
                {products.map((product) => (
                  <ProductCard
                    key={product.sku}
                    product={product}
                    onBrandClick={handleBrandClick}
                    visibleColumns={visibleColumns}
                  />
                ))}
              </div>

              <StatisticsDashboard
                products={products}
                aggregates={aggregates}
                filters={filters}
                onFetchMonthlyData={fetchMonthlyData}
              />

              {/* Pagination Controls */}
              <PaginationControls />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductList;
